import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AdmissionsProcessForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({ title: '', subtitle: '', description: '', steps: [] });
  const [pendingFields, setPendingFields] = useState({ title: false, subtitle: false, description: false });
  const [pendingSteps, setPendingSteps] = useState({});
  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [selectedStepForModal, setSelectedStepForModal] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(`${apiBase}/admissions/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) return;
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach(m => {
          const id = m.id || m.media_id;
          const url = m.url || m.file_path;
          if (id && url) map[id] = map[String(id)] = url;
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AdmissionsProcessForm] Media error:', err.message);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await loadMediaMap();
      
      const response = await fetch(`${apiBase}/admissions/cms/sections`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(response.status);
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const section = result.data.find(s => s.section_type === 'admissions-process');
        if (section) {
          setSectionId(section.id);
          let content = section.content_published;
          if (typeof content === 'string') content = JSON.parse(content);
          const en = content?.en || {};
          const newData = {
            title: en.title || '',
            subtitle: en.subtitle || '',
            description: en.description || '',
            steps: Array.isArray(en.steps) ? en.steps : []
          };
          setFormData(newData);
          setOriginalData(newData);

          const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });
          if (pRes.ok) {
            const pResult = await pRes.json();
            if (pResult.success && pResult.data?.pending_changes) {
              const pMap = { title: false, subtitle: false, description: false };
              const sMap = {};
              pResult.data.pending_changes.forEach(c => {
                if (c.status === 'pending' && c.locale === 'en') {
                  if (['title', 'subtitle', 'description'].includes(c.field_id)) pMap[c.field_id] = true;
                  else if (c.field_id?.includes('steps.')) {
                    const m = c.field_id.match(/steps\.(\d+)\./);
                    if (m) sMap[`step_${m[1]}`] = true;
                  }
                }
              });
              setPendingFields(pMap);
              setPendingSteps(sMap);
            }
          }
        }
      }
    } catch (err) {
      console.error('[AdmissionsProcessForm] Load error:', err.message);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => setFormData(prev => ({...prev, [name]: value}));
  
  const handleStepChange = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === idx ? {...s, [field]: value} : s)
    }));
  };

  const handleImageUpload = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) { toast.error(`Max 5MB`); return; }
    if (!ALLOWED_TYPES.includes(file.type)) { toast.error('Only JPG/PNG/WebP'); return; }

    setImageUploading(p => ({...p, [idx]: true}));
    try {
      const fData = new FormData();
      fData.append('file', file);
      const xhr = new XMLHttpRequest();

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const res = JSON.parse(xhr.responseText);
            if (res.success || res.ok) {
              const mid = res.media_id || res.id;
              if (mid) {
                handleStepChange(idx, 'image', mid);
                toast.success('✓ Image uploaded');
              }
            } else toast.error(res.error || 'Upload failed');
          } catch (e) { toast.error('Invalid response'); }
        } else toast.error('Upload failed');
        setImageUploading(p => ({...p, [idx]: false}));
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload error');
        setImageUploading(p => ({...p, [idx]: false}));
      });

      xhr.open('POST', `${apiBase}/admissions/cms/media/upload?section_type=admissions-process`);
      xhr.withCredentials = true;
      xhr.send(fData);
    } catch (err) {
      toast.error('Error: ' + err.message);
      setImageUploading(p => ({...p, [idx]: false}));
    }
  };

  const handleSubmitField = async (name) => {
    if (!sectionId) { toast.error('No section'); return; }
    if (!formData[name]) { toast.warning(`Fill ${name}`); return; }
    if (formData[name] === originalData[name]) { toast.warning('No changes'); return; }

    setSubmitting(p => ({...p, [name]: true}));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: name,
          old_value: originalData[name],
          new_value: formData[name],
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Submitted');
        setPendingFields(p => ({...p, [name]: true}));
        setOriginalData(p => ({...p, [name]: formData[name]}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [name]: false}));
    }
  };

  const handleSubmitStep = async (idx) => {
    if (!sectionId) { toast.error('No section'); return; }
    const step = formData.steps[idx];
    if (!step?.title) { toast.warning('Fill title'); return; }

    const key = `step_${idx}`;
    setSubmitting(p => ({...p, [key]: true}));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `steps.${idx}.update`,
          field_name: `Step: ${step.title}`,
          old_value: originalData.steps[idx] || {},
          new_value: step,
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Step submitted');
        setPendingSteps(p => ({...p, [key]: true}));
        setOriginalData(p => ({...p, steps: p.steps.map((s, i) => i === idx ? step : s)}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [key]: false}));
    }
  };

  const handleDeleteStep = (idx) => {
    if (window.confirm('Delete step?')) {
      setFormData(prev => ({...prev, steps: prev.steps.filter((_, i) => i !== idx)}));
      setPendingSteps(p => { const u = {...p}; delete u[`step_${idx}`]; return u; });
    }
  };

  const handleAddStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, { title: '', description: '', image: '' }]
    }));
  };

  if (loading) return <div className="hero-form-wrapper"><div className="loading">Loading...</div></div>;

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />
      <div className="hero-form-header">
        <h1 className="hero-form-title">Admissions Process</h1>
        <p className="hero-form-description">Edit title, subtitle, description & process steps</p>
      </div>

      {/* Title */}
      <div className="form-section">
        <div className="field-header">
          <label>Title</label>
          {pendingFields.title && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.title} onChange={e => handleFieldChange('title', e.target.value)} disabled={submitting.title || pendingFields.title} placeholder="Section title..." className="text-input" />
        <button onClick={() => handleSubmitField('title')} disabled={submitting.title || pendingFields.title || formData.title === originalData.title} className="submit-btn">
          {submitting.title ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Subtitle */}
      <div className="form-section">
        <div className="field-header">
          <label>Subtitle</label>
          {pendingFields.subtitle && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.subtitle} onChange={e => handleFieldChange('subtitle', e.target.value)} disabled={submitting.subtitle || pendingFields.subtitle} placeholder="Subtitle..." className="text-input" />
        <button onClick={() => handleSubmitField('subtitle')} disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle} className="submit-btn">
          {submitting.subtitle ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Description */}
      <div className="form-section">
        <div className="field-header">
          <label>Description</label>
          {pendingFields.description && <span className="status-badge pending">Pending</span>}
        </div>
        <textarea value={formData.description} onChange={e => handleFieldChange('description', e.target.value)} disabled={submitting.description || pendingFields.description} placeholder="Description..." className="textarea-input" rows="3" />
        <button onClick={() => handleSubmitField('description')} disabled={submitting.description || pendingFields.description || formData.description === originalData.description} className="submit-btn">
          {submitting.description ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Process Steps */}
      <div className="form-section">
        <div className="items-section-header">
          <h2 className="form-section-title">Process Steps</h2>
          <button type="button" onClick={handleAddStep} className="btn-add-item">+ Add Step</button>
        </div>

        {formData.steps.length === 0 ? (
          <div className="items-empty-state"><p>No steps. Click "Add Step"</p></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {formData.steps.map((step, idx) => {
              const key = `step_${idx}`;
              const isPending = pendingSteps[key];
              return (
                <div key={idx} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Compact Card Image */}
                  <div style={{ height: '180px', background: '#f3f4f6', overflow: 'hidden' }}>
                    {step.image && mediaMap[step.image] ? (
                      <img src={mediaMap[step.image]} alt={`Step ${idx + 1}`} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)', color: '#9ca3af', fontSize: '0.875rem' }}>
                        No Image
                      </div>
                    )}
                  </div>

                  {/* Compact Card Content */}
                  <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#1f2937', lineHeight: 1.3 }}>Step {idx + 1}: {step.title || 'Untitled'}</h3>
                    {step.description && <p style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.5 }}>{step.description.substring(0, 80)}...</p>}
                  </div>

                  {/* Card Actions */}
                  <div style={{ padding: '0.75rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem' }}>
                    <button 
                      type="button"
                      onClick={() => setSelectedStepForModal(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Edit
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleDeleteStep(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Delete
                    </button>
                  </div>

                  {isPending && <div style={{ padding: '0.5rem', background: '#fef08a', color: '#92400e', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600' }}>Pending Approval</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Step Modal */}
      {selectedStepForModal !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onClick={() => setSelectedStepForModal(null)}>
          <div style={{ background: 'white', borderRadius: '12px', maxHeight: '90vh', overflow: 'auto', maxWidth: '700px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>Edit Step</h2>
              <button type="button" onClick={() => setSelectedStepForModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Title */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Title *</label>
                <input 
                  type="text" 
                  value={formData.steps[selectedStepForModal].title || ''} 
                  onChange={e => handleStepChange(selectedStepForModal, 'title', e.target.value)} 
                  placeholder="e.g., Inquiry" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Description</label>
                <textarea 
                  value={formData.steps[selectedStepForModal].description || ''} 
                  onChange={e => handleStepChange(selectedStepForModal, 'description', e.target.value)} 
                  placeholder="What happens in this step..." 
                  rows="3"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Image */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Image</label>
                <input 
                  type="file" 
                  accept="image/jpeg,image/png,image/webp" 
                  onChange={e => handleImageUpload(selectedStepForModal, e)} 
                  disabled={imageUploading[selectedStepForModal]} 
                  id={`step-modal-${selectedStepForModal}`}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
                {imageUploading[selectedStepForModal] && <p style={{ margin: '0.5rem 0 0 0', color: '#3b82f6', fontSize: '0.875rem' }}>Uploading...</p>}
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <button 
                  type="button"
                  onClick={() => handleSubmitStep(selectedStepForModal)}
                  disabled={submitting[`step_${selectedStepForModal}`]}
                  style={{ flex: 1, padding: '0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {submitting[`step_${selectedStepForModal}`] ? 'Submitting...' : 'Submit'}
                </button>
                <button 
                  type="button"
                  onClick={() => setSelectedStepForModal(null)}
                  style={{ flex: 1, padding: '0.75rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
